/**
 * DrumStrategies.js
 * Procedural generation of drum patterns based on SeededConfig.
 * Moves away from static RHYTHM_PATTERNS arrays to generative logic.
 */

import { ABSTRACT_ROLES } from './RoleAllocator.js';
import { getBeatStrength } from './RhythmLogic.js';
import { generateDrumFill } from './OrchestralLogic/TransitionEngine.js';
import { sigmoid } from './NarrativeEngine.js';

// Configuration constants from PercussionLogic (or duplicated for independence)
const DRUMS = {
    KICK: 36,
    SNARE: 38,
    HAT_CLOSED: 42,
    HAT_OPEN: 46
};

// Helper for fuzzy values (using SeededConfig logic style)
const fuzz = (value, variance, random) => {
    if (!random) return value;
    const range = variance * 2;
    return value + ((random.next() * range) - variance);
};

const LOFI_CACHE_KEY = 'lofiDrumPattern';

/**
 * Generates an abstract Lofi drum pattern (dynamic length).
 * Returns array of events relative to bar start.
 */
const generateLofiPattern = (random, context) => {
    const events = [];
    // Dynamic Time Signature Support
    const timeSig = context.timeSig || 4; // Default to 4/4 if simple number
    const numerator = (typeof timeSig === 'number') ? timeSig : (timeSig[0] || 4);
    const denominator = (typeof timeSig === 'number') ? 4 : (timeSig[1] || 4);

    // Calculate total 16th note steps for the bar
    // (Numerator / Denominator) * 4 (quarters) * 4 (16ths)
    // Simplified: Beats * 4
    const beats = numerator * (4 / denominator);
    const totalSteps = Math.ceil(beats * 4); // 16th notes

    const rhythmProfile = (context.config && context.config.rhythmProfile) || { strongBeatWeight: 0.9, weakBeatWeight: 0.1 };

    for (let i = 0; i < totalSteps; i++) {
        const currentBeat = i / 4.0; // Quarter note position
        const strength = getBeatStrength(currentBeat, [numerator, denominator]);

        // --- PHASE 3: Dynamic Timbre Modulation ---
        const tension = context.tension || 0.5;

        // CC Map: Map tension to Hi-Hat Openness (CC 4)
        const ccMap = {
            4: Math.min(127, Math.floor(64 + (tension * 63))) // 64 (Half) to 127 (Open)
        };

        // --- KICK (Bass Drum) ---
        // Prefer Strong beats (Downbeats) + Syncopation
        let kickProb = 0.0;
        if (strength >= 0.9) kickProb = rhythmProfile.strongBeatWeight * 0.9; // Downbeat
        else if (strength >= 0.5) kickProb = rhythmProfile.weakBeatWeight * 0.5; // Backbeat / Mid-beat
        else kickProb = 0.05; // Ghost / Syncopation (Low chance)

        // Custom Lofi Override: Avoid heavy kicks on backbeats (usually beat 2 & 4 in 4/4)
        // Backbeat logic depends on time signature.
        // In 4/4, backbeats are 1.0 (beat 2) and 3.0 (beat 4) (0-indexed 0, 1, 2, 3)
        // Wait, getBeatStrength usually treats 0.0 as strong.
        // Let's assume standard backbeat positions for 4/4.
        const isBackbeat = (Math.abs((currentBeat % 2) - 1.0) < 0.1);

        if (isBackbeat) kickProb *= 0.1; // Reduce kick prob on snare slots

        // Zero & One Rule
        kickProb = Math.max(0.02, Math.min(0.95, kickProb));

        if (random.bool(kickProb)) {
            events.push({
                step: i,
                d: DRUMS.KICK,
                vel: fuzz(0.9, 0.1, random),
                role: ABSTRACT_ROLES.PERC_LOW,
                len: 0.25, // 16th note duration
                articulation: 'kick',
                cc: ccMap
            });
        }

        // --- SNARE (Backbeat) ---
        let snareProb = 0.0;
        if (isBackbeat) snareProb = rhythmProfile.strongBeatWeight;
        else if (strength < 0.4) {
            // Dynamic Ghost Note Probability
            // "Soft Power": Influence instead of override
            const density = (context.config && context.config.vectors) ? context.config.vectors.melodicDensity : 0.5;
            const ghostBase = 0.05;
            // Weighted influence
            snareProb = ghostBase * (1 + (density * 2)) * (1 + (tension * 1.5));
        }

        snareProb = Math.max(0.02, Math.min(0.95, snareProb));

        const hasKick = events.some(e => e.step === i && e.role === ABSTRACT_ROLES.PERC_LOW);
        if (!hasKick && random.bool(snareProb)) {
             let note = DRUMS.SNARE; // 38
             let velocity = isBackbeat ? fuzz(0.85, 0.05, random) : fuzz(0.4, 0.1, random);
             let articulation = isBackbeat ? 'snare' : 'ghost';

             if (velocity < 0.6 && tension < 0.4) {
                 note = 37; // Sidestick
                 articulation = 'sidestick';
             }
             else if (velocity > 0.85 || tension > 0.7) {
                 note = 40; // Rimshot
                 velocity = Math.min(1.0, velocity + 0.1);
                 articulation = 'rimshot';
             }

             events.push({
                step: i,
                d: note,
                vel: velocity,
                role: ABSTRACT_ROLES.PERC_MID,
                len: 0.25, // 16th note duration
                articulation: articulation,
                ghost: !isBackbeat,
                cc: ccMap
            });
        }

        // --- HATS (Closed) ---
        let hatProb = 0.0;
        if (i % 2 === 0) {
            // Fix: "Static Floor" - Replaced 0.8 hardcode with Optimized Sigmoid (k=5)
            // Low tension (0.2) -> 18% (Alive). High tension -> 88% (Breathing).
            hatProb = sigmoid(tension);
        } else {
            // Fix: "Linear Trap" - Replaced Linear with Sigmoid for steep ramp
            // 16ths (dynamic) - Use defaults or tweaked steepness? k=5 is fine.
            hatProb = sigmoid(tension) * 0.8;
        }

        if (random.bool(hatProb)) {
            let note = DRUMS.HAT_CLOSED;
            let articulation = 'hihat_closed';
            if (tension > 0.8 && (i % 4 === 2)) {
                note = DRUMS.HAT_OPEN;
                articulation = 'hihat_open';
            }

            events.push({
                step: i,
                d: note,
                vel: fuzz(0.6, 0.15, random),
                role: ABSTRACT_ROLES.PERC_HIGH,
                len: 0.25, // 16th note duration
                articulation: articulation,
                cc: ccMap
            });
        }
    }

    return events;
};

/**
 * Generates a Lofi / Chill beat.
 * @param {Object} chord - Current chord context.
 * @param {number} barStart - Start time of bar.
 * @param {Object} context - { config, random, bassRhythm }
 */
export const generateLofiBeat = (chord, barStart, context) => {
    const random = context.random;

    // --- MICRO-FORM FILL LOGIC ---
    const isFill = chord.isFill || context.isFill;

    if (isFill && random.bool(0.4)) {
        const intensity = (chord.section === 'CHORUS' || chord.section === 'CLIMAX') ? 'high' : 'medium';
        const fillEvents = generateDrumFill(barStart, intensity);
        return fillEvents.map(e => ({
            ...e,
            role: e.role || ABSTRACT_ROLES.PERC_LOW
        }));
    }

    let patternEvents = null;
    const sectionKey = chord.section || 'default';
    const sectionType = sectionKey.toUpperCase();

    // Extract Time Signature
    const ts = chord.timeSig || (context.timeSig || 4); // Default 4/4
    const tsKey = (typeof ts === 'number') ? `${ts}/4` : `${ts[0]}/${ts[1]}`;

    // Inject Tension & TimeSig into context
    // Fix: "Mainline" - Use Narrative Tension if provided, else fallback to chord tension
    const patternContext = {
        ...context,
        tension: context.narrativeTension !== undefined ? context.narrativeTension : (chord.tension || 0.5),
        timeSig: ts
    };

    const cacheKey = `${sectionKey}_${tsKey}`;

    // --- Fix #2: Rhythm Amnesia (Groove Persistence) ---
    // Check Global Memory (SongMemory) for persisted grooves
    const isPrimary = sectionType.includes('VERSE') || sectionType.includes('INTRO');
    const isRecall = sectionType.includes('OUTRO') || sectionType.includes('REPRISE');
    const memoryKey = `groove_${tsKey}`; // Store by time sig to avoid mismatches

    if (context.songMemory) {
        if (isPrimary && !context.songMemory.motifs[memoryKey]) {
            // Write Mode: Generate and Store
            patternEvents = generateLofiPattern(random, patternContext);
            context.songMemory.motifs[memoryKey] = patternEvents;
            // Also cache locally
            if (context.cache) {
                if (!context.cache[LOFI_CACHE_KEY]) context.cache[LOFI_CACHE_KEY] = {};
                context.cache[LOFI_CACHE_KEY][cacheKey] = patternEvents;
            }
        } else if (isRecall && context.songMemory.motifs[memoryKey]) {
            // Read Mode: Recall from Vault
            patternEvents = context.songMemory.motifs[memoryKey];
            // Optional Variation for Outro? (e.g. less dense)
            if (random.bool(0.3)) {
                // Remove some hats for chill outro
                patternEvents = patternEvents.filter(e => e.role !== ABSTRACT_ROLES.PERC_HIGH || random.bool(0.5));
            }
        }
    }

    // Fallback: Local Cache (Short-term)
    if (!patternEvents) {
        if (context.cache) {
            if (!context.cache[LOFI_CACHE_KEY]) context.cache[LOFI_CACHE_KEY] = {};
            if (!context.cache[LOFI_CACHE_KEY][cacheKey]) {
                context.cache[LOFI_CACHE_KEY][cacheKey] = generateLofiPattern(random, patternContext);
            }
            patternEvents = context.cache[LOFI_CACHE_KEY][cacheKey];
        } else {
            patternEvents = generateLofiPattern(random, patternContext);
        }
    }

    // Instantiate events at barStart
    const events = patternEvents.map(ev => ({
        s: Math.round((barStart + (ev.step * 0.25)) * 1000) / 1000,
        d: ev.d,
        vel: fuzz(ev.vel, 0.05, random),
        role: ev.role,
        len: ev.len,
        articulation: ev.articulation || 'hit',
        ghost: ev.ghost
    }));

    // Soft Lock Injection (Kick to Bass)
    if (context.bassRhythm) {
        context.bassRhythm.forEach(t => {
            // t is relative time
            const absTime = barStart + t;
            // Check if we already have a kick there
            const hasKick = events.some(e => Math.abs(e.s - absTime) < 0.05 && e.role === ABSTRACT_ROLES.PERC_LOW);

            if (!hasKick && random.bool(0.3)) {
                events.push({
                    s: absTime,
                    d: DRUMS.KICK,
                    vel: fuzz(0.75, 0.1, random),
                    role: ABSTRACT_ROLES.PERC_LOW,
                    len: 0.25, // 16th note
                    articulation: 'kick'
                });
            }
        });
    }

    return events;
};

// Dispatcher
export const getDrumStrategy = (strategyName) => {
    switch(strategyName) {
        case 'lofi_procedural': return generateLofiBeat;
        default: return null;
    }
};
